import React from "react";
import { useParams, Link } from "react-router-dom";
import JobCard from "./JobCard";
import useFetch from "../../useFetch";
import { getAuth } from "../../auth";
import EmptyState from "../Common/EmptyState";
import Seo from "../Common/Seo";
import { getSafeExternalUrl } from "../../utils/externalUrls";

const formatJobTime = (date) => {
  if (!date) return "Recently posted";

  const diffMilliseconds = new Date() - new Date(date);
  const diffHours = Math.max(Math.floor(diffMilliseconds / (1000 * 60 * 60)), 0);

  if (diffHours < 1) return "Just now";
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
};

const formatDate = (date) => {
  if (!date) return "Open until filled";

  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatSalary = (job) => {
  if (job.salary_range) return job.salary_range;
  if (job.salary_min && job.salary_max) return `${job.salary_currency ?? "USD"} ${job.salary_min} - ${job.salary_max}`;
  if (job.salary_min) return `From ${job.salary_currency ?? "USD"} ${job.salary_min}`;
  if (job.salary_max) return `Up to ${job.salary_currency ?? "USD"} ${job.salary_max}`;

  return "Not disclosed";
};

const splitTextList = (value) => {
  if (!value) return [];

  return value
    .split(/\r?\n/)
    .map((text) => text.trim())
    .filter(Boolean)
    .map((text, index) => ({ id: `${index}-${text.substring(0, 12)}`, text }));
};

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const getInitials = (name) => {
  const words = String(name || "E")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return words.slice(0, 2).map((word) => word.charAt(0)).join("").toUpperCase() || "E";
};

const mapApiJob = (job) => ({
  id: job.id,
  logo: job.company_logo_url ?? null,
  title: job.title,
  company: job.company_display_name ?? job.company_name ?? "Epic Jobs",
  location: job.location,
  time: formatJobTime(job.published_at ?? job.created_at),
  salary: formatSalary(job),
  salaryPeriod: job.salary_period,
  type: job.job_type,
  workplaceType: job.workplace_type,
  tags: [
    job.job_type,
    job.workplace_type,
    job.category,
    job.is_urgent ? "Urgent" : null,
    job.is_featured ? "Featured" : null,
    (job.effective_status ?? job.status) && (job.effective_status ?? job.status) !== "active" ? job.effective_status ?? job.status : null,
  ].filter(Boolean),
  description: job.description,
  responsibilities: job.responsibilities,
  requirements: job.requirements,
  benefits: job.benefits,
  skills: Array.isArray(job.skills) ? job.skills : [],
  category: job.category,
  experience: job.experience,
  qualification: job.qualification,
  careerLevel: job.career_level,
  vacancies: job.vacancies,
  isSaved: Boolean(job.is_saved),
  contactEmail: job.contact_email,
  companyWebsite: job.company_website,
  companyProfileId: job.company_profile_id,
  applicationDeadline: job.application_deadline,
  listingExpiresAt: job.listing_expires_at,
  hasApplied: Boolean(job.has_applied),
  status: job.effective_status ?? job.status,
  isExpired: Boolean(job.is_expired),
  canApply: job.can_apply !== undefined ? Boolean(job.can_apply) : (job.status === "active" && !job.is_expired),
});

export default function JobDetails() {
  const { id } = useParams();
  const { user, isLoggedIn } = getAuth();
  const { response, isLoading, error: fetchError } = useFetch(process.env.REACT_APP_BASE_API_URL + "/jobs/" + id);
  const { response: relatedResponse } = useFetch(process.env.REACT_APP_BASE_API_URL + "/jobs?per_page=4");

  const job = response?.job ? mapApiJob(response.job) : null;
  const relatedJobs = relatedResponse?.jobs?.data
    ?.filter((item) => item.id !== Number(id))
    ?.slice(0, 3)
    ?.map(mapApiJob) ?? [];

  if (isLoading) {
    return (
      <section className="job-detail-section">
        <Seo title="Job Not Found | Epic-Jobs" description="This Epic-Jobs listing could not be found or is no longer available." />
        <div className="job-detail-outer">
          <div className="auto-container">
            <h5 className="text-center my-4">Loading Data</h5>
          </div>
        </div>
      </section>
    );
  }

  if (fetchError || !job) {
    return (
      <section className="job-detail-section">
        <div className="job-detail-outer">
          <div className="auto-container">
            <EmptyState
              icon="la la-briefcase"
              title="Job not found"
              text={fetchError || "This job may have been deleted, expired, or moved by the employer."}
              actionText="Browse Jobs"
              actionTo="/jobs"
              secondaryText="Go Home"
              secondaryTo="/"
            />
          </div>
        </div>
      </section>
    );
  }

  const dynamicResponsibilities = splitTextList(job.responsibilities);
  const dynamicRequirements = splitTextList(job.requirements);
  const benefitItems = splitTextList(job.benefits);
  const jobSkills = job.skills.map((skill) => ({ id: skill, label: skill }));
  const companyInfo = [
    { id: "industry", label: "Primary industry", value: job.category },
    { id: "experience", label: "Experience", value: job.experience },
    { id: "qualification", label: "Qualification", value: job.qualification },
    { id: "career-level", label: "Career level", value: job.careerLevel },
    { id: "vacancies", label: "Vacancies", value: job.vacancies },
    { id: "email", label: "Email", value: job.contactEmail },
    { id: "location", label: "Location", value: job.location },
  ].filter((item) => hasValue(item.value));
  const jobOverview = [
    { id: "posted", icon: "icon-calendar", label: "Date Posted:", value: job.time },
    { id: "expires", icon: "icon-expiry", label: "Listing expires:", value: formatDate(job.listingExpiresAt) },
    { id: "location", icon: "icon-location", label: "Location:", value: job.location },
    { id: "type", icon: "icon-clock", label: "Job Type:", value: job.type },
    { id: "workplace", icon: "icon-clock", label: "Workplace:", value: job.workplaceType },
    { id: "experience", icon: "icon-user-2", label: "Experience:", value: job.experience },
    { id: "salary", icon: "icon-salary", label: "Salary:", value: job.salary },
    { id: "vacancies", icon: "icon-user-2", label: "Vacancies:", value: job.vacancies },
  ].filter((item) => hasValue(item.value));
  const isCandidate = user?.role === "seeker";
  const isEmployerOrAdmin = ["employer", "admin"].includes(user?.role);
  const safeCompanyWebsite = getSafeExternalUrl(job.companyWebsite);

  return (
    <section className="job-detail-section">
      <Seo
        title={`${job.title} at ${job.company} | Epic-Jobs`}
        description={`Apply for ${job.title} at ${job.company}. Location: ${job.location}. Job type: ${job.type}.`}
      />
      <div className="upper-box">
        <div className="auto-container">
          <div className="job-block-seven">
            <div className="inner-box">
              <div className="content">
                <span className="company-logo">
                  {job.logo ? (
                    <img src={job.logo} alt={job.company} />
                  ) : (
                    <span className="company-logo-initials">{getInitials(job.company)}</span>
                  )}
                </span>
                <h4><Link to={`/jobs/${job.id}`}>{job.title}</Link></h4>
                <ul className="job-info">
                  <li><span className="icon flaticon-briefcase"></span> {job.company}</li>
                  <li><span className="icon flaticon-map-locator"></span> {job.location}</li>
                  <li><span className="icon flaticon-clock-3"></span> {job.time}</li>
                  <li><span className="icon flaticon-money"></span> {job.salary}</li>
                  {job.experience && <li><span className="icon flaticon-user"></span> {job.experience}</li>}
                </ul>
                <ul className="job-other-info">
                  {job.tags.map((tag) => (
                    <li className={tag === "Urgent" ? "required" : tag === "Featured" ? "time" : "privacy"} key={tag}>{tag}</li>
                  ))}
                </ul>
              </div>

              <div className="btn-box">
                {!job.canApply || job.isExpired ? (
                  <button type="button" className="theme-btn btn-style-three" disabled>
                    {job.isExpired ? "Expired" : "Not Accepting Applications"}
                  </button>
                ) : job.hasApplied ? (
                  <button type="button" className="theme-btn btn-style-three" disabled>
                    Already Applied
                  </button>
                ) : isEmployerOrAdmin ? (
                  <button type="button" className="theme-btn btn-style-three" disabled>
                    Candidate Accounts Only
                  </button>
                ) : (
                  <Link to={`/jobs/${job.id}/apply`} className="theme-btn btn-style-one">
                    {isLoggedIn && isCandidate ? "Apply For Job" : "Login to Apply"}
                  </Link>
                )}
                <button className="bookmark-btn" aria-label="bookmark">
                  <i className="flaticon-bookmark"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="job-detail-outer">
        <div className="auto-container">
          <div className="row">
            <div className="content-column col-lg-8 col-md-12 col-sm-12">
              <div className="job-detail">
                <h4>Job Description</h4>
                <p>{job.description}</p>

                {dynamicResponsibilities.length > 0 && (
                  <>
                    <h4>Key Responsibilities</h4>
                    <ul className="list-style-three">
                      {dynamicResponsibilities.map((item) => (
                        <li key={item.id}>{item.text}</li>
                      ))}
                    </ul>
                  </>
                )}

                {dynamicRequirements.length > 0 && (
                  <>
                    <h4>Skill & Experience</h4>
                    <ul className="list-style-three">
                      {dynamicRequirements.map((item) => (
                        <li key={item.id}>{item.text}</li>
                      ))}
                    </ul>
                  </>
                )}

                {benefitItems.length > 0 && (
                  <>
                    <h4>Benefits</h4>
                    <ul className="list-style-three">
                      {benefitItems.map((item) => (
                        <li key={item.id}>{item.text}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              <div className="related-jobs">
                <div className="title-box">
                  <h3>Related Jobs</h3>
                  <div className="text">More roles that match this hiring category.</div>
                </div>
                {relatedJobs.length > 0 ? relatedJobs.map((relatedJob) => (
                  <JobCard job={relatedJob} key={relatedJob.id} />
                )) : (
                  <EmptyState
                    icon="la la-search"
                    title="No related jobs"
                    text="There are no similar active jobs right now."
                    actionText="View All Jobs"
                    actionTo="/jobs"
                  />
                )}
              </div>
            </div>

            <div className="sidebar-column col-lg-4 col-md-12 col-sm-12">
              <aside className="sidebar">
                <div className="sidebar-widget">
                  <h4 className="widget-title">Job Overview</h4>
                  <div className="widget-content">
                    <ul className="job-overview">
                      {jobOverview.map((item) => (
                        <li key={item.id}>
                          <i className={`icon ${item.icon}`}></i>
                          <h5>{item.label}</h5>
                          <span>{item.value}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {jobSkills.length > 0 && (
                    <>
                      <h4 className="widget-title">Job Skills</h4>
                      <div className="widget-content">
                        <ul className="job-skills">
                          {jobSkills.map((skill) => (
                            <li key={skill.id}><Link to="/jobs">{skill.label}</Link></li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}
                </div>

                <div className="sidebar-widget company-widget">
                  <div className="widget-content">
                    <div className="company-title">
                      <div className="company-logo">
                        {job.logo ? (
                          <img src={job.logo} alt={job.company} />
                        ) : (
                          <span className="company-logo-initials">{getInitials(job.company)}</span>
                        )}
                      </div>
                      <h5 className="company-name">{job.company}</h5>
                      <Link to={job.companyProfileId ? `/companies/${job.companyProfileId}` : "/companies"} className="profile-link">View company profile</Link>
                    </div>

                    <ul className="company-info">
                      {companyInfo.map((item) => (
                        <li key={item.id}>
                          {item.label}: <span>{item.value}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="btn-box">
                      {safeCompanyWebsite ? (
                        <a href={safeCompanyWebsite} target="_blank" rel="noreferrer" className="theme-btn btn-style-three">
                          {safeCompanyWebsite}
                        </a>
                      ) : (
                        <Link to={job.companyProfileId ? `/companies/${job.companyProfileId}` : "/companies"} className="theme-btn btn-style-three">View company</Link>
                      )}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
